/**
 * The vocabulary of the usage counters: what may be counted, and what the
 * dashboard gets to read back.
 *
 * This is a *closed* list on purpose, and it is the one thing standing
 * between an open counting endpoint and a table anybody can fill with junk.
 * The client sends a name from here or the server refuses it, so the number
 * of rows the counters can ever grow to is `days × events × surfaces` and
 * not "whatever somebody posted".
 *
 * Nothing here identifies anybody. A counter is a day, a name, a surface and
 * a number — there is no room in that shape for a person, which is why it is
 * the shape.
 */
/**
 * Which shell a request came out of.
 *
 * This is the question the whole feature exists to answer — "do people use
 * the app or the website?" — and it is the installed PWA versus a browser
 * tab, read from the display mode the browser itself reports. `unknown` is
 * everything that never said: a crawler, a curl, a client cached from before
 * this shipped.
 */
export const USAGE_SURFACES = ['app', 'web', 'unknown'];
/**
 * Header the client marks its own shell with. Declared here rather than on
 * either side of the wire, because a header only one half spells correctly is
 * a statistic that silently reads `unknown` forever.
 */
export const SURFACE_HEADER = 'x-filmsloth-surface';
export function isUsageSurface(value) {
    return USAGE_SURFACES.includes(value);
}
/**
 * The screens a client may report having opened.
 *
 * Deliberately the app's own screen names rather than URLs: FilmSloth is one
 * page with a handful of standing addresses (see `web/src/lib/routes.ts`), so
 * a path list would say almost nothing — everything interesting happens at
 * `/`.
 */
export const USAGE_VIEWS = [
    'home',
    'create',
    'join',
    'lobby',
    'swipe',
    'runoff',
    'results',
    'finished',
    'workshop',
    'film',
    'watchlist',
    'login',
    'account',
    'device',
    'stats',
    'about',
    'contact',
    'privacy',
    'impressum',
];
export function isUsageView(value) {
    return USAGE_VIEWS.includes(value);
}
/** The counter name a screen is tallied under. */
export function viewEvent(view) {
    return `view:${view}`;
}
/**
 * Things that are counted, beyond the per-screen tallies above.
 *
 * `visit` is one page load, reported once by the client. Everything else is
 * recorded by the server when the action actually succeeds — a swipe deck
 * that was started, a session somebody joined — which is why those numbers
 * cannot be inflated by anything a browser claims, and why they keep working
 * for a client that blocks the ping.
 */
export const USAGE_EVENTS = [
    'visit',
    'session_created',
    'session_joined',
    'session_started',
    'session_matched',
    'film_chosen',
    'vote_cast',
    'account_created',
    'film_suggested',
    'fact_claimed',
    'trailer_submitted',
];
export function isUsageEvent(value) {
    return USAGE_EVENTS.includes(value);
}
/**
 * Per-day numbers that are *not* counters at all: they are counted out of the
 * tables that were always there — when accounts, devices and films were
 * created. Which means they reach back to before any of this was built,
 * where a counter necessarily starts at zero on the day it ships.
 */
export const DERIVED_USAGE_KEYS = ['new_devices', 'new_accounts', 'new_films'];
